/**
 * Design B HQ: "Open now" in Dubai time, computed in the browser because
 * Liquid output is cached, and a map that loads only when asked for.
 */
import { scene, revealAll } from '@theme/clinic-motion';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL = { Sun: 'Sunday', Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday' };

const hourLabel = (h) => {
  const suffix = h >= 12 ? 'pm' : 'am';
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return `${twelve}${suffix}`;
};

function dubaiNow() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Dubai',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return { day: get('weekday'), hour: Number(get('hour')), minute: Number(get('minute')) };
}

class ClinicHQ extends HTMLElement {
  connectedCallback() {
    this.renderStatus();
    this.timer = setInterval(() => this.renderStatus(), 60_000);

    this.querySelector('[data-map-load]')?.addEventListener('click', (event) => {
      const button = event.currentTarget;
      const frame = document.createElement('iframe');
      frame.src = button.dataset.src;
      frame.loading = 'lazy';
      frame.referrerPolicy = 'no-referrer-when-downgrade';
      frame.title = 'Map';
      button.replaceWith(frame);
    });

    this.dispose = scene(this, ({ scroller }) => revealAll(this, { scroller }));
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.dispose?.();
  }

  renderStatus() {
    const el = this.querySelector('[data-status]');
    const text = this.querySelector('[data-status-text]');
    if (!el || !text || this.hasAttribute('data-status-off')) return;

    const open = Number(this.dataset.open);
    const close = Number(this.dataset.close);
    const closedDays = (this.dataset.closedDays || '').split(',').map((d) => d.trim()).filter(Boolean);
    if (Number.isNaN(open) || Number.isNaN(close)) return;

    const now = dubaiNow();
    const isOpenDay = !closedDays.includes(now.day);
    const isOpen = isOpenDay && now.hour >= open && now.hour < close;

    if (isOpen) {
      text.textContent = `Open now · closes ${hourLabel(close)}`;
      el.dataset.state = 'open';
    } else {
      let index = DAYS.indexOf(now.day);
      let label = 'today';
      if (!isOpenDay || now.hour >= close) {
        for (let i = 1; i <= 7; i++) {
          const next = DAYS[(index + i) % 7];
          if (!closedDays.includes(next)) {
            label = i === 1 ? 'tomorrow' : FULL[next];
            break;
          }
        }
      }
      text.textContent = `Closed now · opens ${hourLabel(open)} ${label}`;
      el.dataset.state = 'closed';
    }
    el.hidden = false;
  }
}

if (!customElements.get('clinic-hq')) customElements.define('clinic-hq', ClinicHQ);
